import { useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import {
  fetchPostApiIntroducerPortalIntroducerApplicationRefresh,
  PostApiIntroducerPortalIntroducerApplicationRefreshRequestBody,
} from '@iwoca/lapi-client/introducer_portal';
import { Button, Message } from '@iwoca/orion';
import { t, Trans } from '@lingui/macro';
import { LoadingModal } from 'components/LoadingModal/LoadingModal';
import { useReviewOffer } from 'components/ReviewOffer/ReviewOffer.context';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { useNavigate } from 'react-router-dom';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';

export const RefreshOfferButton = () => {
  const {
    selectedApplication: {
      customer,
      requested_products,
      formal_offer_sets,
      application_uid,
    },
  } = useReviewOffer();
  const { introducerId, checkIntroducerPermission } = useIntroducerContext();
  const { trackEvent } = useAmplitude();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const offer = formal_offer_sets[0].formal_offers[0];
  const requestedAmount = requested_products[0].amount;

  const refreshOffer = async () => {
    if (introducerId) {
      try {
        setIsLoading(true);
        setHasError(false);

        const requestedProduct: PostApiIntroducerPortalIntroducerApplicationRefreshRequestBody['data']['requested_products'][number] =
          {
            amount: requestedAmount,
            product_type: offer.product_type,
            purpose: requested_products[0].purpose,
            detailed_purpose: requested_products[0].detailed_purpose,
            duration: offer.duration,
          };

        const applicationResponse =
          await fetchPostApiIntroducerPortalIntroducerApplicationRefresh({
            applicationId: application_uid,
            introducerId,
            body: {
              data: {
                requested_products: [requestedProduct],
              },
            },
          });

        trackEvent(AMPLITUDE_EVENTS.applicationRefreshed);

        // redirect to newly created application
        navigate(
          PATH_NAMES.application({
            applicationId: applicationResponse.data.application_id,
          }),
        );
      } catch (e) {
        setHasError(true);
        setIsLoading(false);

        if (typeof window !== 'undefined') {
          datadogLogs.logger.error('Failed to refresh application', {
            createError: e as string,
            stateKey: customer.state_key,
            amount: offer.max_amount,
            productType: offer.product_type,
          });
        }
      }
    }
  };

  if (customer.has_open_application || customer.has_active_formal_offer) {
    return null;
  }

  return (
    <>
      <Button
        variant="denim"
        size="small"
        loading={isLoading}
        disabled={!checkIntroducerPermission('can_refresh_offers')}
        onClick={refreshOffer}
        data-testid="refresh-application-button"
      >
        <Trans>Refresh</Trans>
      </Button>
      {isLoading && (
        <LoadingModal
          heading="We're refreshing your application"
          text="This may take up to 60 seconds."
          showModal={isLoading}
        />
      )}
      {hasError && <Message message={t`Something went wrong`} type="error" />}
    </>
  );
};
